<template>
  <ul v-if="earliestPurchaseDate">
    <li v-for="attr in attributesToDisplay" v-bind:class="{ conditional: attr.IsConditional }" v-bind:key="attr.id">
      <template v-if="!attr.IsDisabled && !attr.IsHidden && (!user.SignedIn || !attr.IsHiddenSignedIn)">
        <div
          class="attribute-description"
          v-if="showAttributeDescriptions && attr.Description"
          v-html="attr.Description"
        ></div>
        <!-- We have a slot for each type of control -->
        <!-- <attribute> object as a slot prop -->
        <slot
          name="radio-buttons"
          v-bind:attr="attr"
          v-if="attr.AttributeControlType == controlTypes.RADIO_BUTTON_LIST"
        >
          <!-- Fallback content -->
          <form-radio-buttons
            v-model="attr.SelectedValueId"
            v-on:value-changed="(val) => setAttributeSelectedValueId(attr, val)"
            :id="attr.Id"
            :options="attributeOptionsById[attr.Id]"
            :control-id="'product_attribute_' + attr.Id"
          >
            <template slot="option-name" slot-scope="{ optionValue }">
              <div
                class="option-content"
                :class="{
                  'has-overlay': optionValue.isReload && !user.SignedIn,
                }"
              >
                <reload-signin-message v-if="optionValue.isReload && !user.SignedIn"></reload-signin-message>
                <div class="option-photo" v-if="optionValue.photo">
                  <img :src="optionValue.photo" />
                </div>
                <div class="option-description">
                  <h3 class="title">{{ optionValue.title }}</h3>
                  <h4 class="subhead" v-html="optionValue.subhead"></h4>
                </div>
                <div class="option-price" v-if="optionValue.priceAdjustmentValue !== 0">
                  <span v-if="optionValue.priceAdjustmentValue > 0">+</span>
                  <span v-else-if="optionValue.priceAdjustmentValue < 0">-</span>
                  {{ optionValue.priceAdjustmentValue | removeCurrencyFraction(language, currency) }}
                </div>
              </div>
            </template>
          </form-radio-buttons>
        </slot>
        <slot
          name="dropdown-list"
          v-bind:attr="attr"
          v-else-if="attr.AttributeControlType == controlTypes.DROP_DOWN_LIST && !attr.IsActivityTime"
        >
          <!-- Fallback content -->
          <form-select
            :required="attr.IsRequired"
            :id="attr.Id"
            :options="attributeOptionsById[attr.Id]"
            :placeholder="filters.stripAttributeFields(attr.TextPrompt)"
            :label="filters.attributeHeadline(attr.Name)"
            v-model="attr.SelectedValueId"
            v-on:value-changed="(val) => setAttributeSelectedValueId(attr, val)"
          >
            <template slot="option-name" slot-scope="{ optionValue }">
              {{ optionValue.text }}
            </template>
          </form-select>
        </slot>
        <slot name="dropdown-list" v-bind:attr="attr" v-else-if="attr.IsActivityTime">
          <!-- Fallback content -->
          <product-activity-time>
            <form-select
              :animated-label="false"
              :id="attr.Id"
              :label="filters.attributeHeadline(attr.Name)"
              :options="
                availableTimes.map((item) => {
                  return { value: item.value, name: item.name }
                })
              "
              :placeholder="filters.stripAttributeFields(attr.TextPrompt)"
              :required="attr.IsRequired"
              :show-placeholder="true"
              slot-scope="{ availableTimes, selectedDate, setTimeSlot }"
              v-if="selectedDate"
              v-model="attr.SelectedValueId"
              v-on:value-changed="
                (val) => {
                  setTimeSlot(val)
                  setAttributeSelectedValueId(attr, val)
                }
              "
            >
              <template slot="option-name" slot-scope="{ optionValue }">
                {{ optionValue.name }}
              </template>
            </form-select>
          </product-activity-time>
        </slot>
        <slot name="checkboxes" v-bind:attr="attr" v-else-if="attr.AttributeControlType == controlTypes.CHECKBOXES">
          <!-- Fallback content -->
          <form-radio-buttons
            v-model="attr.SelectedValueId"
            v-on:value-changed="(val) => setAttributeSelectedValueId(attr, val)"
            :id="attr.Id"
            :is-checkboxes="true"
            :options="attributeOptionsById[attr.Id]"
            :control-id="'product_attribute_' + attr.Id"
          >
            <template slot="option-name" slot-scope="{ optionValue }">
              <div
                class="option-content"
                :class="{
                  'has-overlay': optionValue.isReload && !user.SignedIn,
                }"
              >
                <reload-signin-message v-if="optionValue.isReload && !user.SignedIn"></reload-signin-message>
                <div class="option-photo" v-if="optionValue.photo">
                  <img :src="optionValue.photo" />
                </div>
                <div class="option-description">
                  <h3 class="title">{{ optionValue.title }}</h3>
                  <h4 class="subhead" v-html="optionValue.subhead"></h4>
                </div>
                <div class="option-price" v-if="optionValue.priceAdjustmentValue !== 0">
                  <span v-if="optionValue.priceAdjustmentValue > 0">+</span>
                  <span v-else-if="optionValue.priceAdjustmentValue < 0">-</span>
                  <span>{{ optionValue.priceAdjustmentValue | removeCurrencyFraction(language, currency) }}</span>
                </div>
              </div>
            </template>
          </form-radio-buttons>
        </slot>
        <slot
          name="textinput"
          v-bind:attr="attr"
          v-else-if="attr.AttributeControlType == controlTypes.READ_ONLY_TEXTBOX"
        >
          <!-- Fallback content -->
          <form-input
            :control-id="'product_attribute_' + attr.Id"
            :id="attr.Id"
            :label="filters.attributeHeadline(attr.Name)"
            v-model="attr.SelectedValue"
            :read-only="true"
          ></form-input>
        </slot>
        <slot
          name="textinput"
          v-bind:attr="attr"
          v-on:value-changed="attributeValueChanged"
          v-else-if="attr.AttributeControlType == controlTypes.TEXTBOX"
        >
          <!-- Fallback content -->
          <form-input
            :control-id="'product_attribute_' + attr.Id"
            :id="attr.Id"
            :label="filters.attributeHeadline(attr.Name)"
            v-model="attr.SelectedValue"
            v-on:value-changed="attributeValueChanged"
          ></form-input>
        </slot>
        <slot
          name="date-picker"
          v-bind:attr="attr"
          v-else-if="attr.AttributeControlType == controlTypes.DATEPICKER && !datesAsList && product.IsActivity"
        >
          <!-- Fallback content -->
          <product-activity-date :initial-date="getProductInitialDate(attr)">
            <form-date-picker
              :calendar-data-hash="activityDataHash"
              :calendar-data-updating="activityDataUpdating"
              :control-id="`product_attribute_${attr.Id}`"
              :default-date="getProductDefaultDate(attr)"
              :initial-date="getProductInitialDate(attr)"
              :label="filters.attributeHeadline(attr.Name)"
              :locale="locale"
              :max-date="seasonEnd.toDate()"
              :min-date="earliestPurchaseDate.startOf('day').toDate()"
              :multiple-dates="isMultipleDates(attr)"
              :placeholder="filters.stripAttributeFields(attr.TextPrompt)"
              :render-cell="renderSimpleDate"
              :display-inline="isInlineCalendar(attr)"
              slot-scope="{ activityDataUpdating, activityDataHash, updateActivityTimes, renderSimpleDate }"
              v-model="attr.SelectedValueId"
              v-on:date-changed="
                (value) => {
                  dateChanged(value, attr)
                }
              "
              v-on:month-changed="updateActivityTimes"
              v-on:value-changed="(val) => setAttributeSelectedValueId(attr, val)"
            ></form-date-picker>
          </product-activity-date>
        </slot>
        <slot
          name="datepicker"
          v-bind:attr="attr"
          v-else-if="attr.AttributeControlType == controlTypes.DATEPICKER && !datesAsList && !product.IsActivity"
        >
          <!-- Fallback content -->
          <product-inventory-date :inventory-ready="inventoryReady">
            <form-date-picker
              :calendar-data-hash="inventoryDataHash"
              :calendar-data-updating="inventoryDataUpdating"
              :control-id="`product_attribute_${attr.Id}`"
              :default-date="getProductDefaultDate(attr)"
              :initial-date="getProductInitialDate(attr)"
              :label="filters.attributeHeadline(attr.Name)"
              :locale="locale"
              :max-date="seasonEnd.toDate()"
              :min-date="earliestPurchaseDate.startOf('day').toDate()"
              :multiple-dates="isMultipleDates(attr)"
              :placeholder="filters.stripAttributeFields(attr.TextPrompt)"
              :render-cell="renderSimpleDate"
              :display-inline="isInlineCalendar(attr)"
              slot-scope="{ inventoryDataHash, inventoryDataUpdating, updateMonthInventory, renderSimpleDate }"
              v-model="attr.SelectedValueId"
              v-on:date-changed="
                (value) => {
                  dateChanged(value, attr)
                }
              "
              v-on:month-changed="updateMonthInventory"
              v-on:value-changed="(val) => setAttributeSelectedValueId(attr, val)"
            ></form-date-picker>
          </product-inventory-date>
        </slot>
        <slot
          name="date-picker"
          v-bind:attr="attr"
          v-else-if="attr.AttributeControlType == controlTypes.DATEPICKER && datesAsList && purchasableInventory"
        >
          <!-- Fallback content -->
          <form-radio-buttons
            v-model="attr.SelectedValue"
            v-on:value-changed="setRadioButtonDate"
            :id="attr.Id"
            :options="attributeOptionsById[attr.Id]"
            :label="attr.Name"
            :autoselect-first="true"
            :control-id="'product_attribute_' + attr.Id"
          >
            <template slot="option-name" slot-scope="{ optionValue }">
              <div class="option-description">
                {{ optionValue.name | dayOfWeekDate(locale.language, false) }}
              </div>
            </template>
          </form-radio-buttons>
          <form-input
            v-on:value-changed="attributeValueChanged"
            v-model="attr.SelectedYear"
            :id="attr.Id + '_year'"
            :label="filters.attributeHeadline(attr.Name)"
            :control-id="'product_attribute_' + attr.Id + '_year'"
            v-show="false"
          ></form-input>
          <form-input
            v-on:value-changed="attributeValueChanged"
            v-model="attr.SelectedMonth"
            :id="attr.Id + '_month'"
            :label="filters.attributeHeadline(attr.Name)"
            :control-id="'product_attribute_' + attr.Id + '_month'"
            v-show="false"
          ></form-input>
          <form-input
            v-on:value-changed="attributeValueChanged"
            v-model="attr.SelectedDay"
            :id="attr.Id + '_day'"
            :label="filters.attributeHeadline(attr.Name)"
            :control-id="'product_attribute_' + attr.Id + '_day'"
            v-show="false"
          ></form-input>
        </slot>
      </template>
    </li>
  </ul>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'

import FormDatePicker from '@/components/form/form-date-picker'
import FormInput from '@/components/form/form-input'
import FormSelect from '@/components/form/form-select'
import FormRadioButtons from '@/components/form/form-radio-buttons'
import ProductActivityDate from '@/components/product-activity-date'
import ProductInventoryDate from '@/components/product-inventory-date'
import ProductActivityTime from '@/components/product-activity-time'
import InventoryDatePicker from '@/components/inventory-date-picker'
import ReloadSigninMessage from '@/components/reload-signin-message'
import _ from 'lodash'

export default {
  name: 'product-attributes',
  components: {
    FormInput,
    FormSelect,
    FormRadioButtons,
    FormDatePicker,
    ProductActivityDate,
    ProductInventoryDate,
    ProductActivityTime,
    InventoryDatePicker,
    ReloadSigninMessage,
  },
  data() {
    return {
      controlTypes: {
        CHECKBOXES: 3,
        COLOR_SQUARES: 40,
        DATEPICKER: 20,
        DROP_DOWN_LIST: 1,
        FILE_UPLOAD: 30,
        IMAGE_SQUARES: 45,
        MULTILINE_TEXTBOX: 5,
        RADIO_BUTTON_LIST: 2,
        READ_ONLY_CHECKBOXES: 50,
        TEXTBOX: 4,
        READ_ONLY_TEXTBOX: 99,
      },
      dateSelected: false,
      isUpdatingAttributeValue: false,
      updatingAttributeValueId: 0,
      attributeValueChangedTimeout: null,
    }
  },

  methods: {
    isMultipleDates(attr) {
      return attr.IsMultipleDates || this.isNonConsecutiveMultiDay
    },
    isInlineCalendar(attr) {
      // This is used to show a calendar inline - displayed without requiring the user to pop-up the view.
      // Set using the text prompt value on the attribute - [InlineCalendar] .. defaults to true if the product is an activity
      // otherwise, false
      return attr.IsInlineCalendar ? attr.IsInlineCalendar : this.product.IsActivity
    },

    getProductInitialDate(attr) {
      let productInitialDate = this.earliestPurchaseDate

      if (attr.DefaultValue) {
        const defaultDate = new Date(attr.DefaultValue).getDate() + 1
        const defaultMonth = new Date(attr.DefaultValue).getMonth()
        const defaultYear = new Date(attr.DefaultValue).getFullYear()
        // Per moment guidelines, the date must always be set as year > month > date, to avoid errors
        productInitialDate = this.timeAtResort
          .clone()
          .local()
          .set({ year: defaultYear, month: defaultMonth, date: defaultDate })
          .startOf('day')
          .utcOffset(this.storeTimezoneOffset)
      }

      if (productInitialDate.isBefore(this.earliestPurchaseDate)) {
        productInitialDate = this.earliestPurchaseDate.clone()
      }

      return new Date(productInitialDate.year(), productInitialDate.month(), productInitialDate.date())
    },

    getProductDefaultDate(attr) {
      if (attr.DefaultValue) {
        let productDefaultDate = null
        const defaultDate = moment(attr.DefaultValue).date()
        const defaultMonth = moment(attr.DefaultValue).month()
        const defaultYear = moment(attr.DefaultValue).year()
        // Per moment guidelines, the date must always be set as year > month > date, to avoid errors
        productDefaultDate = this.timeAtResort.clone().year(defaultYear).month(defaultMonth).date(defaultDate)

        if (productDefaultDate.startOf('day').isSameOrAfter(this.earliestPurchaseDate)) {
          return new Date(defaultYear, defaultMonth, defaultDate)
        }
      }

      return null
    },

    mapAttributeOptions(attr) {
      return (attr?.Values || []).map((item) => ({
        value: item.Id || 0,
        name: item.Name || '',
        hidden: !!item.IsFiltered,
        isReload: !!item.IsReload,
        isPreSelected: this.isAttributePreselected(attr?.Id || 0, item.Id || 0, item.IsPreSelected),
        photo: item.ImageSquaresPictureModel ? item.ImageSquaresPictureModel.FullSizeImageUrl : undefined,
        title: this.filters.attributeHeadline(item.Name),
        subhead: this.filters.attributeSubHeadline(item.Name),
        priceAdjustment: item.PriceAdjustment,
        priceAdjustmentValue: item.PriceAdjustmentValue,
      }))
    },

    mapAttributeOptionsForDates() {
      return Object.keys(this.purchasableInventory)
        .filter((key) => {
          const dateParts = key.split('-')
          const d1 = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]))
          const d2 = new Date(
            this.earliestPurchaseDate.year(),
            this.earliestPurchaseDate.month(),
            this.earliestPurchaseDate.date()
          )
          // Only show dates that are on or after the earliest purchase date
          if (d1 < d2) return false
          // Only show dates that have purchasable inventory available
          if (this.purchasableInventory[key] <= 0) return false
          return true
        })
        .map((key) => {
          return {
            value: key,
            name: key,
            title: this.filters.dayOfWeekDate(key, this.locale.language),
          }
        })
    },

    /*
     * Checks if an attribute should be preselected based on the route query parameters
     * returns fallback value if no query params map to any attribute items
     */
    isAttributePreselected(itemId, itemValue, fallback = false) {
      const routeQueryObject = this.$route.query
      const potentialPresetInQuery = Object.hasOwn(routeQueryObject, String(itemId))

      if (!potentialPresetInQuery) return fallback

      return Number(routeQueryObject[itemId] || 0) === itemValue
    },

    attributeValueChanged(newValue, updateInventory) {
      // Debounce the attribute value changed event
      if (this.attributeValueChangedTimeout) clearTimeout(this.attributeValueChangedTimeout)
      this.attributeValueChangedTimeout = setTimeout(() => {
        this.updateAttributeValue(newValue, updateInventory)
      }, 500)
    },

    /**
     * Update the attribute value and optionally update the inventory after setting the attribute value.
     * @param newValue The new value to set for the attribute
     * @param updateInventory Whether to update the inventory after setting the attribute value
     */
    updateAttributeValue(newValue, updateInventory) {
      const update = updateInventory === undefined ? true : updateInventory
      const id = ++this.updatingAttributeValueId
      this.isUpdatingAttributeValue = true
      this.$emit('attributeValueChanged', newValue)
      this.$store.dispatch('products/updateProductFilteredValues')
      this.$store.dispatch('products/updateProductAttributes', update).finally(() => {
        // If the id has changed, it means another updateAttributeValue was called
        if (id !== this.updatingAttributeValueId) return
        this.isUpdatingAttributeValue = false
      })
    },

    dateChanged(date, attr) {
      if (!date) {
        this.$store.commit('products/setAttributeValueProps', {
          selectedAttributeId: attr.ProductAttributeId,
          selectedDates: null,
          selectedDay: null,
          selectedMonth: null,
          selectedYear: null,
        })
        store.commit('products/setSelectedProductDates', [])
        return
      }
      const { month, day, year, dateList } = date
      this.$store.commit('products/setAttributeValueProps', {
        selectedAttributeId: attr.ProductAttributeId,
        selectedDates: dateList,
        selectedDay: day,
        selectedMonth: month,
        selectedYear: year,
      })
      this.$store.dispatch('products/resetActivityTime')
      this.$store.dispatch('products/resetSelectedProductQuantity')
      this.$store.commit('products/setSelectedProductDates', dateList)
      this.dateSelected = true
    },

    async setRadioButtonDate(newValue, updateInventory) {
      await this.$store.dispatch('products/setRadioButtonDate', newValue)
      // ensure selected value is set for date attribute
      this.attributeValueChanged(newValue, updateInventory)
    },

    setAttributeSelectedValueId(attr, valueId) {
      this.$store.commit('products/setAttributeSelectionData', {
        SelectedAttributeId: attr.ProductAttributeId,
        SelectedValueId: valueId,
        SelectedValue: undefined,
        Selected: !!valueId,
      })
      this.attributeValueChanged(valueId, true)
    },
  },
  computed: {
    productAttributes() {
      return this.attributes
    },

    filters() {
      return this.$options.filters
    },

    product() {
      return this.selectedProduct || this.attributesProduct
    },

    earliestPurchaseDate() {
      return this.selectedProductEarliestPurchaseDate || this.product.EarliestPurchaseDate
    },

    inventoryReady() {
      return !!(this.product && this.product.EarliestPurchaseDate && this.product.Inventory)
    },

    /**
     * Determines which attributes should actually be displayed
     * This is so the attribute label will not be displayed if having no visible values will cause attribute to be hidden
     */
    attributesToDisplay() {
      return this.productAttributes.filter((attr) => {
        return (
          attr.Values?.some((val) => !val.IsFiltered) ||
          attr.AttributeControlType === this.controlTypes.TEXTBOX ||
          attr.AttributeControlType === this.controlTypes.MULTILINE_TEXTBOX ||
          attr.AttributeControlType === this.controlTypes.DATEPICKER ||
          attr.AttributeControlType === this.controlTypes.FILE_UPLOAD ||
          attr.AttributeControlType === this.controlTypes.READ_ONLY_TEXTBOX
        )
      })
    },

    disabledAttributes() {
      return this.productAttributes.filter((attr) => attr.IsDisabled)
    },

    /**
     * Maps and formats the Attribute Option values for display
     */
    attributeOptionsById() {
      return this.productAttributes.reduce((map, attr) => {
        if (typeof attr.Id !== 'number') return map
        let options = []
        if (
          attr.AttributeControlType === this.controlTypes.RADIO_BUTTON_LIST ||
          attr.AttributeControlType === this.controlTypes.CHECKBOXES
        ) {
          options = this.mapAttributeOptions(attr)
        } else if (attr.AttributeControlType === this.controlTypes.DROP_DOWN_LIST && !attr.IsActivityTime) {
          options = this.filters.getAttributeValues(attr, this.product.IsReservationProduct)
        } else if (attr.AttributeControlType === this.controlTypes.DATEPICKER && this.purchasableInventory) {
          options = this.mapAttributeOptionsForDates()
        }
        map[attr.Id] = options
        return map
      }, {})
    },

    ...mapGetters({
      currency: 'global/currency',
      datesAsList: 'products/selectedProductIsRadioButtonDate',
      selectedProductEarliestPurchaseDate: 'products/selectedProductEarliestPurchaseDate',
      inventory: 'products/selectedProductInventory',
      purchasableInventory: 'products/selectedProductPurchasableInventory',
      isNonConsecutiveMultiDay: 'products/selectedProductIsNonConsecutiveMultiDay',
      language: 'global/language',
      locale: 'global/locale',
      selectedProduct: 'products/selectedProduct',
      selectedCategory: 'products/selectedCategoryName',
      seasonEnd: 'global/seasonEnd',
      seasonStart: 'global/seasonStart',
      timeAtResort: 'global/timeAtResort',
      storeTimezoneOffset: 'global/storeTimezoneOffset',
      user: 'global/user',
    }),
  },
  watch: {
    attributeOptionsById: {
      immediate: true,
      handler(newVal, oldVal) {
        // Watch for attributes that no longer have options and reset the selected value to
        // avoid invalid selections
        for (const attr of this.productAttributes) {
          if (!attr.Id) continue
          const previouslyHadOptions = !!(oldVal && oldVal[attr.Id] && oldVal[attr.Id].length)
          const nowHasOptions = !!(newVal && newVal[attr.Id] && newVal[attr.Id].length)
          // Check if the attribute had options and now does not
          if (previouslyHadOptions && !nowHasOptions) {
            // Depending on the control type, we may clear the selected values differently
            if (attr.AttributeControlType === this.controlTypes.DATEPICKER && this.datesAsList)
              this.setRadioButtonDate(null)
          }

          // if options changed, preselect first preselect value
          // otherwise, clear the selected value
          if (newVal && oldVal && !_.isEqual(newVal[attr.Id], oldVal[attr.Id])) {
            const preselected = newVal[attr.Id].find((option) => option.isPreSelected)
            if (preselected) {
              this.setAttributeSelectedValueId(attr, preselected.value)
            } else if (!attr.IsDate) {
              // we don't want to clear date selections here
              this.setAttributeSelectedValueId(attr, null)
            }
          }
        }
      },
    },
    disabledAttributes: {
      handler(newVal, oldVal) {
        if (!newVal) return
        // Watch for attributes that are disabled and reset the selected value to
        // avoid invalid selections
        for (const attr of this.productAttributes) {
          if (!attr.Id) continue
          const disabledAttr = newVal.find((a) => a.Id === attr.Id)
          const previouslyEnabled = !!(oldVal && !oldVal.some((a) => a.Id === attr.Id))
          const nowDisabled = !!disabledAttr
          if (previouslyEnabled && nowDisabled) {
            const preselected =
              disabledAttr && disabledAttr.Values ? disabledAttr.Values.find((val) => val.IsPreSelected) : null
            if (preselected) {
              this.setAttributeSelectedValueId(attr, preselected.Id)
            } else {
              this.setAttributeSelectedValueId(attr, null)
            }
          }
        }
      },
    },
  },
  props: {
    attributes: {
      type: Array,
      default: [],
    },
    attributesProduct: {
      type: Object,
      default: null,
    },
    showAttributeDescriptions: {
      type: Boolean,
      default: false,
    },
  },
}
</script>
